import React, { useEffect, useRef, useState } from 'react';

import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Badge } from '@/components/ui/badge';
import { AvatarWithName } from '../../../../../../../shared/components/Avatar';
import { TagList } from '../../../../../../shared/components/Tag';
import { TASK_PRIORITIES } from '../../../../constants';
import { taskShape } from '../../../../types';
import { formatDate } from '../../../../utils';
import DropdownMenu from './DropdownMenu';

const TaskCard = ({ onDeleteClick, onEditClick, statusId, task }) => {
  const ref = useRef(null);

  const [dragging, setDragging] = useState(false);

  const { color: priorityColor, icon: priorityIcon } =
    TASK_PRIORITIES[task.priority || 'none'];

  useEffect(() => {
    const element = ref.current;

    const handleDrop = (element) => {
      setDragging(false);
      triggerPostMoveFlash(element);
    };

    return draggable({
      element,
      getInitialData: () => ({ id: task.id, statusId, type: 'task' }),
      onDragStart: () => setDragging(true),
      onDrop: () => handleDrop(element),
    });
  }, [task.id, statusId]);

  return (
    <div
      className="flex items-center px-3.5 py-2.5 transition-colors hover:bg-muted gap-5"
      ref={ref}
      style={dragging ? { opacity: 0.4 } : {}}
    >
      <div className="flex items-center w-full gap-4">
        <FontAwesomeIcon
          className={`shrink-0 ${priorityColor}`}
          icon={priorityIcon}
          size="sm"
        />

        <div className="flex items-center gap-2">
          <span className="truncate text-foreground">{task.name}</span>

          {!isEmpty(task.caseFile?.name) && (
            <Badge className="shrink-0 border-border bg-surface text-muted-foreground">
              {task.caseFile.name}
            </Badge>
          )}
        </div>
      </div>

      <TagList className="min-w-48" tags={task.tags} />

      <div className="min-w-40">
        {task.assignee && (
          <AvatarWithName
            avatarColor={task.assignee.avatarColor}
            fullName={task.assignee.fullName}
            initials={task.assignee.initials}
          />
        )}
      </div>

      <span className="min-w-24 text-foreground">{formatDate(task.createdAt)}</span>

      <DropdownMenu
        onDeleteClick={onDeleteClick}
        onEditClick={onEditClick}
        taskId={task.id}
      />
    </div>
  );
};

TaskCard.propTypes = {
  onDeleteClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  statusId: PropTypes.string.isRequired,
  task: taskShape.isRequired,
};

export default TaskCard;
